import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";
import {
  ActiveClient,
  BlueprintContentBundle,
  BlueprintContentField,
  CustomContentRow,
  NewsletterSource,
} from "../types/contentTools";

/**
 * Operator-only client for the Content Tools page. Backed by
 * Api::V1::ContentToolsController, which is gated on the operator session.
 */
class ContentToolsService {
  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  /** List active clients (blueprints with a delivery cadence, system-wide). */
  async getActiveClients(): Promise<ActiveClient[]> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/content_tools/active_clients`,
      { headers: this.getAuthHeaders() },
    );
    return response.data.clients;
  }

  /** Fetch the full content bundle (all four artifacts) for a blueprint. */
  async getBlueprintContentBundle(
    blueprintId: string,
  ): Promise<BlueprintContentBundle> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}`,
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /**
   * Save raw BeeFree JSON to one of the blueprint's content columns
   * (content_row, link_dump_row, or default_manual_post_template).
   */
  async updateBlueprintContent(
    blueprintId: string,
    field: BlueprintContentField,
    value: object,
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}`,
      { [field]: value },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Set (or clear) the blueprint's footer row index. `index` is 0-based. */
  async updateBlueprintFooterIndex(
    blueprintId: string,
    index: number | null,
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}`,
      { beefree_row_footer_index: index },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Save the BeeFree JSON of a single custom content row template. */
  async updateCustomContentRow(
    id: string,
    beefreeTemplate: object,
  ): Promise<CustomContentRow> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/custom_content_rows/${id}`,
      { beefree_template: beefreeTemplate },
      { headers: this.getAuthHeaders() },
    );
    return response.data;
  }

  /** Set or clear the custom content row template linked to one section. */
  async linkTemplateToSection(
    blueprintId: string,
    sectionIndex: number,
    templateId: string | null,
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/section_link_template`,
      { section_index: sectionIndex, template_id: templateId },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Set or clear the ingredient group linked to one section. */
  async updateSectionGroup(
    blueprintId: string,
    sectionIndex: number,
    orgIngredientGroupId: string | null,
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/section_group`,
      { section_index: sectionIndex, org_ingredient_group_id: orgIngredientGroupId },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Set per-section sourcing config (instructions / count / source mode). */
  async updateSectionConfig(
    blueprintId: string,
    sectionIndex: number,
    config: {
      instructions?: string;
      required_articles_count?: number;
      source_selection_mode?: string;
    },
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/section_config`,
      { section_index: sectionIndex, ...config },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Copy content artifacts (and optionally custom rows) from another blueprint. */
  async copyFromBlueprint(
    blueprintId: string,
    sourceBlueprintId: string,
    copyCustomContentRows: boolean,
  ): Promise<void> {
    await axios.post(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/copy_from_blueprint`,
      {
        source_blueprint_id: sourceBlueprintId,
        copy_custom_content_rows: copyCustomContentRows,
      },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Set (or clear) an ingredient group's preferred content row template. */
  async updateIngredientGroup(
    id: string,
    preferredContentRowTemplateId: string | null,
  ): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/ingredient_groups/${id}`,
      { preferred_content_row_template_id: preferredContentRowTemplateId },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Add a newsletter source to the blueprint's organization. */
  async createSource(
    blueprintId: string,
    payload: {
      url: string;
      title?: string;
      instructions?: string;
      preferred_section_indexes?: number[];
    },
  ): Promise<NewsletterSource> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/sources`,
      payload,
      { headers: this.getAuthHeaders() },
    );
    return response.data.source;
  }

  /** Update a source (scoped to the blueprint's organization). */
  async updateSource(
    blueprintId: string,
    sourceId: string,
    payload: {
      url?: string;
      title?: string;
      instructions?: string;
      preferred_section_indexes?: number[];
    },
  ): Promise<NewsletterSource> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/sources/${sourceId}`,
      payload,
      { headers: this.getAuthHeaders() },
    );
    return response.data.source;
  }

  /** Delete a source (scoped to the blueprint's organization). */
  async deleteSource(blueprintId: string, sourceId: string): Promise<void> {
    await axios.delete(
      `${getApiUrlWithVersion()}/content_tools/blueprints/${blueprintId}/sources/${sourceId}`,
      { headers: this.getAuthHeaders() },
    );
  }
}

const contentToolsService = new ContentToolsService();
export default contentToolsService;
